<template>
  <div class="container-fluid py-4">
    <!-- Breadcrumb and Title -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center">
          <router-link to="/salary" class="text-decoration-none">
           
          </router-link>
          <span class="text-muted me-2">Salary Details</span>
          
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="row">
      <div class="col-12 text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">Loading salary details...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="row">
      <div class="col-12">
        <div class="alert alert-danger" role="alert">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="row">
      <div class="col-12">
        <!-- Salary Info Card -->
        <div class="card mb-4 shadow-sm">
          <div class="card-header cap-header mx-3 mt-3 d-flex align-items-center">
            <h6 class="text-white text-capitalize m-0">
              Salary Details - {{ salaryTitle }}
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <p class="mb-0"><strong>Title:</strong> {{ salaryTitle }}</p>
              </div>
              <div class="col-md-6">
                <p class="mb-0"><strong>Date:</strong> {{ salaryDate }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Salary Details Table -->
        <div class="card shadow-sm">
          <div class="card-header cap-header mx-3 mt-3 d-flex align-items-center">
            <h6 class="text-white text-capitalize m-0">
              Employee Salary Details ({{ salaryDetails.length }} Employees)
            </h6>
          </div>

          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0">Project</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0">Employee</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0">Monthly Salary</th>
                    <th class="px-4 py-3 text-secondary fw-semibold border-0">Salary Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="detail in salaryDetails"
                    :key="detail.id"
                    class="border-bottom"
                  >
                    <td class="px-4 py-3">
                      <span class="text-muted">{{ detail.project }}</span>
                    </td>
                    <td class="px-4 py-3">
                      <span class="fw-medium">{{ detail.employee }}</span>
                    </td>
                    <td class="px-4 py-3">
                      <span class="text-muted">{{ formatCurrency(detail.monthly_salary) }}</span>
                    </td>
                    <td class="px-4 py-3">
                      <div class="salary-amount-container">
                        <input
                          v-if="editingId === detail.id"
                          v-model="editingValue"
                          type="number"
                          step="0.01"
                          min="0"
                          class="form-control salary-input"
                          @blur="saveAmount(detail.id)"
                          @keyup.enter="saveAmount(detail.id)"
                          @keyup.escape="cancelEdit"
                          ref="editInput"
                        />
                        <div
                          v-else
                          class="salary-display"
                          :class="{ 'editable': !isUpdating }"
                          @click="startEdit(detail.id, detail.salary_amount)"
                        >
                          <span class="salary-value">{{ formatCurrency(detail.salary_amount) }}</span>
                          <i v-if="!isUpdating" class="fas fa-edit edit-icon ms-2 text-muted"></i>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="salaryDetails.length === 0">
                    <td colspan="4" class="text-center py-4 text-muted">
                      No salary details found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="row mt-4">
          <div class="col-12 text-end">
            <router-link to="/salary" class="btn btn-outline-secondary me-2">
              <i class="fas fa-arrow-left me-1"></i>
              Back to List
            </router-link>
            <button
              class="btn btn-cap"
              @click="saveAllChanges"
              :disabled="!hasChanges || isUpdating"
            >
              <i v-if="isUpdating" class="fas fa-spinner fa-spin me-1"></i>
              <i v-else class="fas fa-save me-1"></i>
              {{ isUpdating ? 'Saving...' : 'Save All Changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import axios from '@/api'

const route = useRoute()
const toast = useToast()

// Reactive data
const loading = ref(true)
const error = ref('')
const salaryDetails = ref([])
const salaryTitle = ref('')
const salaryDate = ref('')
const editingId = ref(null)
const editingValue = ref('')
const isUpdating = ref(false)
const hasChanges = ref(false)
const editInput = ref(null)

const salaryId = route.params.id

// Fetch salary details
const fetchSalaryDetails = async () => {
  loading.value = true
  error.value = ''

  try {
    const response = await axios.get(`/financial-requests/salaries/${salaryId}/details`)
    const data = response.data
    salaryDetails.value = data.data || []

    // Get salary info from the main salary list
    const salaryResponse = await axios.get('/financial-requests/salaries')
    const salaryInfo = (salaryResponse.data.data || []).find(s => String(s.id) === String(salaryId))

    if (salaryInfo) {
      salaryTitle.value = salaryInfo.title || 'Unknown'
      salaryDate.value = salaryInfo.date || 'Unknown'
    }
  } catch (err) {
    console.error('Error fetching salary details:', err)
    error.value = 'Failed to load salary details. Please try again.'
  } finally {
    loading.value = false
  }
}

// Start editing a salary amount
const startEdit = (detailId, currentAmount) => {
  if (isUpdating.value) return
  editingId.value = detailId
  editingValue.value = String(currentAmount)

  nextTick(() => {
    if (editInput.value && editInput.value[0]) {
      editInput.value[0].focus()
      editInput.value[0].select()
    }
  })
}

// Cancel editing
const cancelEdit = () => {
  editingId.value = null
  editingValue.value = ''
}

// Save individual amount
const saveAmount = async (detailId) => {
  if (!editingValue.value || parseFloat(editingValue.value) < 0) {
    toast.error('Please enter a valid amount')
    return
  }

  isUpdating.value = true
  try {
    const payload = { salary_amount: parseFloat(editingValue.value) }
    await axios.patch(`/financial-requests/salary-details/update-amounts/${detailId}`, payload)

    // Update local data
    const detailIndex = salaryDetails.value.findIndex(d => d.id === detailId)
    if (detailIndex !== -1) {
      salaryDetails.value[detailIndex].salary_amount = parseFloat(editingValue.value)
    }

    hasChanges.value = true
    toast.success('Salary amount updated successfully')
  } catch (err) {
    console.error('Error updating salary amount:', err)
    toast.error('Failed to update salary amount')
  } finally {
    isUpdating.value = false
    editingId.value = null
    editingValue.value = ''
  }
}

// Save all changes (placeholder for future bulk operations)
const saveAllChanges = () => {
  toast.success('All changes have been saved!')
  hasChanges.value = false
}

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount || 0)
}

onMounted(() => {
  fetchSalaryDetails()
})
</script>

<style scoped>
/* CAP red band (same spirit as Contracts page) */
.cap-header {
  background: linear-gradient(195deg, #b03a45, #8d2e38);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 0.75rem 1rem;
}

/* CAP red button */
.btn-cap {
  background-color: #b03a45;
  border-color: #b03a45;
  color: #fff;
}
.btn-cap:hover {
  background-color: #8d2e38;
  border-color: #8d2e38;
  color: #fff;
}
.btn-cap:disabled {
  opacity: 0.65;
  pointer-events: none;
}

/* Editable amount UI */
.salary-amount-container {
  position: relative;
  min-height: 30px;
}

.salary-display {
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.salary-display.editable {
  cursor: pointer;
  background-color: #f8f9fa;
  border: 1px solid transparent;
}

.salary-display.editable:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.salary-input {
  width: 140px;
  padding: 6px 12px;
  border: 2px solid #b03a45;
  border-radius: 6px;
  font-weight: 500;
}
.salary-input:focus {
  outline: none;
  border-color: #8d2e38;
  box-shadow: 0 0 0 0.2rem rgba(176, 58, 69, 0.25);
}

.salary-value {
  font-weight: 500;
  color: #495057;
}

.edit-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 12px;
}
.salary-display.editable:hover .edit-icon {
  opacity: 0.6;
}

/* Card and table cosmetics */
.card {
  border: none;
  border-radius: 12px;
}
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}
.table th {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}
.table td {
  vertical-align: middle;
}
</style>
